
import { apiCall } from '../utils/apiUtils';
import { User } from '../types';
import enhancedAuthService from './enhanced-auth-service';

// Enhanced API call with CORS error handling
const apiCallWithCORSHandling = async (url: string, options: any = {}): Promise<any> => {
  try {
    const response = await apiCall(url, options);
    return response;
  } catch (error: any) {
    console.warn('API call failed, possibly due to CORS:', error);
    
    // If it's a CORS error, return a fallback response
    if (error.message?.includes('CORS') || 
        error.message?.includes('blocked') ||
        error.message?.includes('preflight') ||
        error.name === 'TypeError') {
      
      console.log('🔄 Returning fallback data for CORS error');
      
      // Return error instead of fake data for targeting/analytics
      if (url.includes('targeting') || url.includes('analytics')) {
        throw new Error('Service temporarily unavailable due to CORS');
      }
      
      // Default fallback
      return { success: false, error: 'CORS_ERROR', fallback: true };
    }
    
    throw error;
  }
};

const API_BASE_URL = 'https://a3d1kmonz2.execute-api.us-east-1.amazonaws.com/dev';

export interface FollowResponse {
  success: boolean;
  isFollowing: boolean;
  followersCount: number;
  followingCount: number;
}

export interface BlockResponse {
  success: boolean;
  isBlocked: boolean;
}

export const followUser = async (userId: string): Promise<FollowResponse> => {
  try {
    const response = await apiCallWithCORSHandling(`${API_BASE_URL}/social/follow`, {
      method: 'POST',
      body: JSON.stringify({ userId })
    });
    return response;
  } catch (error) {
    console.error('Error following user:', error);
    throw error;
  }
};

export const unfollowUser = async (userId: string): Promise<FollowResponse> => {
  try {
    const response = await apiCallWithCORSHandling(`${API_BASE_URL}/social/unfollow`, {
      method: 'POST',
      body: JSON.stringify({ userId })
    });
    return response;
  } catch (error) {
    console.error('Error unfollowing user:', error);
    throw error;
  }
};

export const getFollowStatus = async (userId: string): Promise<boolean> => {
  try {
    const response = await apiCallWithCORSHandling(`${API_BASE_URL}/social/follow-status/${userId}`);
    return response.isFollowing || false;
  } catch (error) {
    console.error('Error getting follow status:', error);
    return false;
  }
};

export const getFollowers = async (userId: string, limit: number = 20, cursor?: string): Promise<{ users: User[], hasMore: boolean, nextCursor?: string }> => {
  try {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    if (cursor) params.append('cursor', cursor);

    const response = await apiCallWithCORSHandling(`${API_BASE_URL}/social/followers/${userId}?${params.toString()}`);
    return response;
  } catch (error) {
    console.error('Error fetching followers:', error);
    return { users: [], hasMore: false };
  }
};

export const getFollowing = async (userId: string, limit: number = 20, cursor?: string): Promise<{ users: User[], hasMore: boolean, nextCursor?: string }> => {
  try {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    if (cursor) params.append('cursor', cursor);

    const response = await apiCallWithCORSHandling(`${API_BASE_URL}/social/following/${userId}?${params.toString()}`);
    return response;
  } catch (error) {
    console.error('Error fetching following:', error);
    return { users: [], hasMore: false };
  }
};

export const blockUser = async (userId: string): Promise<BlockResponse> => {
  try {
    const response = await apiCallWithCORSHandling(`${API_BASE_URL}/social/block`, {
      method: 'POST',
      body: JSON.stringify({ userId })
    });
    return response;
  } catch (error) {
    console.error('Error blocking user:', error);
    throw error;
  }
};

export const unblockUser = async (userId: string): Promise<BlockResponse> => {
  try {
    const response = await apiCallWithCORSHandling(`${API_BASE_URL}/social/unblock`, {
      method: 'POST',
      body: JSON.stringify({ userId })
    });
    return response;
  } catch (error) {
    console.error('Error unblocking user:', error);
    throw error;
  }
};

export const getBlockStatus = async (userId: string): Promise<boolean> => {
  try {
    const response = await apiCallWithCORSHandling(`${API_BASE_URL}/social/block-status/${userId}`);
    return response.isBlocked || false;
  } catch (error) {
    console.error('Error getting block status:', error);
    return false;
  }
};

export const getFollowingFeed = async (limit: number = 20, cursor?: string): Promise<{ outfits: any[], hasMore: boolean, nextCursor?: string }> => {
  try {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    if (cursor) params.append('cursor', cursor);

    const response = await apiCallWithCORSHandling(`${API_BASE_URL}/social/following-feed?${params.toString()}`);
    return response;
  } catch (error) {
    console.error('Error fetching following feed:', error);
    return { outfits: [], hasMore: false };
  }
};

export const checkLiked = async (outfitId: string): Promise<boolean> => {
  try {
    // First check if user is authenticated
    const isAuthenticated = await enhancedAuthService.isAuthenticated();
    if (!isAuthenticated) {
      console.log('User not authenticated for liked status check - returning false');
      return false;
    }

    // Get a fresh token
    const token = await enhancedAuthService.getAccessToken();
    if (!token) {
      console.log('No valid token available for liked status check - returning false');
      return false;
    }

    // Make authenticated API call with better error handling
    const response = await apiCall(`${API_BASE_URL}/outfits/${outfitId}/liked`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }) as any;
    
    return response.isLiked || response.liked || false;
  } catch (error: any) {
    // Handle 401 errors gracefully - user might not be authenticated
    if (error.response?.status === 401 || error.message?.includes('401')) {
      console.log('User not authenticated for liked status check - returning false');
      return false;
    }
    
    // Handle other errors gracefully
    if (error.response?.status === 403) {
      console.log('Access forbidden for liked status check - returning false');
      return false;
    }
    
    if (error.response?.status === 404) {
      console.log('Liked status endpoint not found - returning false');
      return false;
    }
    
    console.error('Error checking liked status:', error);
    return false;
  }
};

export const likeOutfit = async (outfitId: string): Promise<boolean> => {
  try {
    const response = await apiCallWithCORSHandling(`${API_BASE_URL}/outfits/${outfitId}/like`, {
      method: 'POST'
    });
    
    // Check for successful response
    if (response && (response.liked === true || response.success === true)) {
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error liking outfit:', error);
    throw error;
  }
};

export const unlikeOutfit = async (outfitId: string): Promise<boolean> => {
  try {
    const response = await apiCallWithCORSHandling(`${API_BASE_URL}/outfits/${outfitId}/unlike`, {
      method: 'POST'
    });
    
    // Check for successful response
    if (response && (response.liked === false || response.success === true)) {
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error unliking outfit:', error);
    throw error;
  }
};

export const getShareUrl = (outfitId: string, platform: 'twitter' | 'facebook' | 'pinterest'): string => {
  const baseUrl = window.location.origin;
  const outfitUrl = `${baseUrl}/outfit/${outfitId}`;
  const encodedUrl = encodeURIComponent(outfitUrl);
  const encodedText = encodeURIComponent('Check out this amazing outfit!');

  switch (platform) {
    case 'twitter':
      return `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`;
    case 'facebook':
      return `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
    case 'pinterest':
      return `https://pinterest.com/pin/create/button/?url=${encodedUrl}&description=${encodedText}`;
    default:
      return outfitUrl;
  }
};

// Legacy class export for compatibility
export class SocialService {
  static followUser = followUser;
  static unfollowUser = unfollowUser;
  static getFollowStatus = getFollowStatus;
  static getFollowers = getFollowers;
  static getFollowing = getFollowing;
  static blockUser = blockUser;
  static unblockUser = unblockUser;
  static getBlockStatus = getBlockStatus;
  static getFollowingFeed = getFollowingFeed;
  static checkLiked = checkLiked;
  static likeOutfit = likeOutfit;
  static unlikeOutfit = unlikeOutfit;
  static getShareUrl = getShareUrl;
}
